import type { SearchRequest, SearchResponse } from '@vue-storefront/prestashop-api';
import { useSdk } from '~/sdk';

interface SearchState {
  query: string;
  page: number;
  data: SearchResponse['psdata'] | null;
  loading: boolean;
  error: string | null;
}

export const useSearch = () => {
  const { locale } = useI18n();
  const route = useRoute();
  let controller = new AbortController();

  const state = useState<SearchState>(
    'searchState',
    (): SearchState => ({
      query: route.query.s ? String(route.query.s) : '',
      page: route.query.page ? Number(route.query.page) : 1,
      data: { products: [], facets: [] },
      loading: false,
      error: null,
    }),
  );

  const search = async (options: Pick<SearchRequest, 'facets'|'sort'|'exclude' > = { facets: {}, exclude: null }) => {
    if (controller) controller.abort();
    controller = new AbortController();

    state.value.loading = true;
    state.value.error = null;

    try {
      const data = await useSdk().commerce.search(
        {
          s: state.value.query as string,
          page: state.value.page,
          ...options,
        },
        { signal: controller.signal },
      );

      if (data) {
        state.value.data = data;
      }

      state.value.loading = false;
    } catch (error) {
      state.value.error = (error as Error).message || 'Search failed';
    } finally {
      if (state.value.error) {
        state.value.loading = false;
      }
    }
  };

  watch(() => route.query.s, () => {
    if (state.value.query === route.query.s) return;
    state.value.query = route.query.s as string;
  });

  watch(locale, () => search());

  return {
    ...toRefs(state.value),
    loading: computed(() => state.value.loading),
    data: computed(() => state.value.data),
    facets: computed(() => state.value.data?.facets),
    search,
  };
};
